import { findDuplicates } from 'global/common/dataDiff'
import _ from 'lodash'
import { useCallback } from 'react'
import { useEventInfo } from './useEventInfo'
import { useAppUserAccess } from './useAppUserAccess'

export const _validationIssueTypes = {
  null: 'null',
  invalidNumber: 'invalidNumber',
  emptyString: 'emptyString',
  emptyArray: 'emptyArray',
  duplicateArrayItems: 'duplicateArrayItems',
  duplicateItems: 'duplicateItems',
  emptyObject: 'emptyObject',
  missingArrayKeys: 'missingArrayKeys',
}

export const _validationActionTypes = {
  remove: 'remove',
  replace: 'replace'
}

const _testing = false

export const useCleanData = () => {

  const appUserAccess = useAppUserAccess()
  const { isSuperAdminDeveloper } = appUserAccess ?? {}

  const { eventInfo } = useEventInfo()
  const { eventDups } = eventInfo ?? {}

  const cleanUpData = useCallback(({
    data,
    removeUnderscores = false,
    removeProps = [],
    arrayOptions = {
      dedupe: true,
      collapseSingles: false,
      objectKey: '_itemKey'
    },
    fixTimeFormat = true,
    fixBooleans = true
  }) => {
    return cleanForFirebase(data, { removeUnderscores, removeProps, arrayOptions, fixTimeFormat, fixBooleans })
  }, [])

  const validateData = useCallback(({ data, appDataKey, refVals }) => {
    return validateTheData(data, appDataKey, refVals, isSuperAdminDeveloper, eventDups)
  }, [])

  const validateAppData = useCallback(({ appDataDocuments }) => {
    const _vlds = {}
    const refVals = validateReferences(appDataDocuments)
    // loop the docs
    _.forEach(appDataDocuments, (appData, appDataKey) => (
      _vlds[appDataKey] = validateData({ data: appData, appDataKey, refVals })
    ))
    return _vlds
  }, [])

  return { cleanUpData, validateData, validateAppData }

}

const cleanForFirebase = (
  data,
  {
    removeUnderscores = false,
    removeProps = [],
    arrayOptions = {
      dedupe: true,
      collapseSingles: false,
      objectKey: '_itemKey'
    },
    fixTimeFormat = true,
    fixBooleans = true,
  } = {}
) => {
  const removePropsSet = new Set(removeProps)
  const seenArrays = new WeakSet()

  const isEmptyValue = (v) =>
    v === null ||
    v === undefined ||
    (typeof v === 'string' && v.trim() === '') ||
    (Array.isArray(v) && v.length === 0) ||
    (_.isPlainObject(v) && _.isEmpty(v))

  const isInvalidNumber = (v) =>
    typeof v === 'number' && !isFinite(v)

  const cleanArray = (arr) => {
    const { dedupe, collapseSingles, objectKey } = arrayOptions

    let cleaned = arr
      .map(item => traverse(item))
      .filter(v => !isEmptyValue(v) && !isInvalidNumber(v))

    // dedupe
    if (dedupe) {
      const seen = new Set()

      cleaned = cleaned.filter(item => {
        const key = _.isPlainObject(item)
          ? item?.[objectKey] ?? JSON.stringify(item)
          : item

        if (seen.has(key)) return false
        seen.add(key)
        return true
      })
    }

    // collapse single-item arrays
    if (collapseSingles && cleaned.length === 1) {
      return cleaned[0]
    }

    return cleaned
  }

  const traverse = (val) => {
    if (isEmptyValue(val) || isInvalidNumber(val)) return undefined

    if (Array.isArray(val)) {
      if (seenArrays.has(val)) return undefined
      seenArrays.add(val)
      return cleanArray(val)
    }

    if (fixTimeFormat && _.isString(val)) {
      const timeMatch = val.match(/^(\d{2}:\d{2}):\d{2}$/)
      if (timeMatch) {
        return timeMatch[1]
      }
    }

    if (fixBooleans && _.isString(val)) {
      const lower = val.toLowerCase();

      if (lower === 'true') return true;
      if (lower === 'false') return false;
    }

    if (_.isPlainObject(val)) {
      return _.reduce(
        val,
        (acc, v, key) => {
          if (removeUnderscores && key.startsWith('_') && key !== '_itemKey') return acc
          if (removePropsSet.has(key)) return acc

          const cleaned = traverse(v)

          if (isEmptyValue(cleaned) || isInvalidNumber(cleaned)) return acc

          acc[key] = cleaned
          return acc
        },
        {}
      )
    }

    return val
  }

  const result = traverse(data)


  // final safety pass
  if (isEmptyValue(result)) return undefined

  return result
}

const validateTheData = (
  data,
  appDataKey,
  refVals,
  isSuperAdminDeveloper,
  eventDups,
  {
    ignoreUnderscoreProps = true,
    checkEmptyStrings = true,
    checkNulls = true,
    checkEmptyArrays = true,
    checkEmptyObjects = true,
    checkDuplicates = true,
    fixTimeFormat = true,
    objectKey = '_itemKey'
  } = {}
) => {

  const _data = _.cloneDeep(data)

  if (_testing && appDataKey === 'attendees') {
    const att1 = '1A4D4EDAFB01D8B60BE5'
    const att2 = '0071F5E509DBDAD37AAE'
    _data[att1].agencyId = ''
    _data[att1].primaryOrAdditionalGuestTypes = []
    _data[att1].sessions.push('189')
    _data[att2].sessions.push('152')
    _data[att2].primaryOrAdditionalGuestTypes = []
  }

  const issues = []
  const issueGroups = {}

  const addIssue = (type, path, value, updateDataValue = null) => {
    if (!path) return;
    issues.push({
      type,
      path,
      value,
      updateDataKey: path,
      updateDataValue
    })
  }

  const addItemIssue = (type, path, value, updateDataValue = null, dups = null, rfz = null) => {
    if (!path) return;
    if (!issueGroups[type]) { issueGroups[type] = [] }
    const fullPath = path ? `${appDataKey}.${path}` : appDataKey
    const _path = path.split('.')
    const existing = _.find(issueGroups[type], { fullPath })
    if (existing) return;
    issueGroups[type].push({
      fullPath,
      path,
      appDataItemKey: _path?.[0],
      itemProp: _.last(_path),
      appDataKey,
      updateDataValue: updateDataValue || null,
      dups,
      rfz,
      value,
    })
  }

  const walk = (value, path = '', og = null) => {
    // null / undefined
    if (checkNulls && _.isNil(value)) {
      addIssue('NULL', path, value, null)
      // addItem(_validationIssueTypes.null, path)
      return
    }

    // NaN / Infinity
    if (_.isNumber(value) && !_.isFinite(value)) {
      addIssue(_validationIssueTypes.invalidNumber, path, value, null)
      // addItem(_validationIssueTypes.invalidNumber, path)
      // return
    }

    // empty string
    if (checkEmptyStrings &&
      _.isString(value) &&
      _.trim(value) === ''
    ) {
      addIssue(_validationIssueTypes.emptyString, path, value, null)
      addItemIssue(_validationIssueTypes.emptyString, path, value)
      // return
    }

    // arrays
    if (_.isArray(value)) {
      if (checkEmptyArrays && _.isEmpty(value)) {
        addIssue(_validationIssueTypes.emptyArray, path, value, null)
        addItemIssue(_validationIssueTypes.emptyArray, path, value)
      }

      if (checkDuplicates && !_.isEmpty(value)) {
        const seen = new Set()

        _.forEach(value, item => {
          const dedupeKey = _.isPlainObject(item)
            ? _.get(item, objectKey) ?? JSON.stringify(item)
            : item

          if (seen.has(dedupeKey)) {

            addIssue(
              _validationIssueTypes.duplicateArrayItems,
              path,
              dedupeKey,
              _.uniq(value)
            )

            addItemIssue(
              _validationIssueTypes.duplicateArrayItems,
              path,
              dedupeKey,
              _.uniq(value)
            )

            if (isSuperAdminDeveloper) {
              console.log('array value', value)
              console.log('og', og)
              console.log('eventDups', eventDups)
            }

          }

          seen.add(dedupeKey)
        })
      }

      _.forEach(value, (item, index) => {
        walk(item, `${path}[${index}]`, value)
      })

      // return
    }

    // objects
    if (_.isPlainObject(value)) {
      if (checkEmptyObjects && _.isEmpty(value)) {
        addIssue(_validationIssueTypes.emptyObject, path, value)
        addItemIssue(_validationIssueTypes.emptyObject)
        return
      }

      _.forOwn(value, (child, key) => {
        if (
          ignoreUnderscoreProps &&
          _.startsWith(key, '_')
        ) {
          return
        }

        walk(
          child,
          path ? `${path}.${key}` : key,
          value
        )
      })

      return
    }
  }

  walk(_data)

  const dups = findDuplicates(_data)

  if (!_.isEmpty(dups)) {
    addItemIssue(_validationIssueTypes.duplicateItems, appDataKey, null, null, dups)
  }

  const rfz = _.filter(refVals, { docKey: appDataKey })
  if (!_.isEmpty(rfz)) {
    addItemIssue(_validationIssueTypes.missingArrayKeys, appDataKey, null, null, dups, rfz)
  }

  return {
    valid: _.isEmpty(issues),
    isValid: _.isEmpty(issueGroups),
    issueTypeCount: _.size(issueGroups),
    issueCount: _.size(issues),
    summary: _.countBy(issues, 'type'),
    _summary: _.mapValues(issueGroups, (iss) => _.size(iss)),
    issues,
    issueGroups,
    dups,
    rfz
  }
}

// const findDuplicatesXX = (obj) => {
//   const groups = new Map();

//   const norm = (v) =>
//     (v ?? "")
//       .toString()
//       .toLowerCase()
//       .replace(/\s+/g, "")
//       .trim();

//   const makeKey = (item) => {
//     const startDate = norm(item.startDate);
//     const startTime = norm(item.startTime);

//     if (item.name) {
//       return `name:${norm(item.name)}|${startDate}|${startTime}`;
//     }

//     if (item.firstName && item.lastName) {
//       return `person:${norm(item.firstName)}|${norm(item.lastName)}|${startDate}|${startTime}`;
//     }

//     return null;
//   };

//   // Group duplicates
//   _.forEach(obj, (item, key) => {
//     const groupKey = makeKey(item);
//     if (!groupKey) return;

//     if (!groups.has(groupKey)) {
//       groups.set(groupKey, []);
//     }

//     groups.get(groupKey).push({ key, item });
//   });

//   const result = {};

//   groups.forEach((items) => {
//     if (items.length < 2) return;

//     // Prefer an item that has non-empty arrays
//     const original =
//       _.find(items, ({ item }) => hasRelationships(item)) || items[0];

//     result[original.key] = {
//       originalItem: original.item,
//       duplicateItems: items
//         .filter((x) => x.key !== original.key)
//         .map((x) => x.item),
//     };
//   });

//   return result;
// };

// const findDuplicatesXXX = (obj) => {
//   const seen = new Map();
//   const result = {};

//   const norm = (v) =>
//     (v ?? '')
//       .toString()
//       .toLowerCase()
//       .replace(/\s+/g, '')
//       .trim();

//   const makeKey = (item) => {

//     const startDate = norm(item.startDate);
//     const startTime = norm(item.startTime);

//     if (item.name) {
//       return `name: ${norm(item.name)
//         }| ${startDate} | ${startTime}`;
//     }

//     if (item.firstName && item.lastName) {
//       return `person: ${norm(item.firstName)
//         }| ${norm(item.lastName)
//         }| ${startDate}| ${startTime} `;
//     }

//     return null;
//   };

//   _.forEach(obj, (item, key) => {

//     const k = makeKey(item);
//     if (!k) return;

//     const existing = seen.get(k);

//     // FIRST time we see this key → store as original
//     if (!existing) {
//       seen.set(k, { key, item });
//       return;
//     }

//     // ensure group exists once we hit first duplicate
//     const originalKey = existing.key;

//     if (!result[originalKey]) {
//       result[originalKey] = {
//         originalItem: existing.item,
//         duplicateItems: [],
//       };
//     }

//     // ALWAYS push every duplicate occurrence
//     result[originalKey].duplicateItems.push(item);
//   });

//   return result;
// };

function validateReferences(appDataDocuments) {
  const errors = [];
  const appDataDocKeys = _.keys(appDataDocuments);

  _.forEach(appDataDocuments, (appDataDocument, appDocKey) => {

    _.forEach(appDataDocument, (appDataDoc, appDataDocKey) => {

      const { _itemKey, name } = appDataDoc ?? {}

      _.forEach(appDataDocKeys, appDataDocKey => {

        const refs = appDataDoc[appDataDocKey];

        if (!_.isArray(refs)) return;

        _.forEach(refs, ref => {

          if (!_.has(appDataDocuments, [appDataDocKey, ref]) && ref !== 'none') {
            errors.push({
              docKey: appDocKey,
              arrayKey: appDataDocKey,
              missingKey: ref,
              _itemKey,
              name,
              originalItem: appDataDoc
              // related: appDataDocuments?.[appDataDocKey]?.[ref]
            });
          }

        });

      });

    });

  });

  return errors;
}